import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { testStudents } from '../data/testStudents';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface HeaderProps {
  isTransparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isTransparent = false }) => {
  const { user, logout, loginAsStudent } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<number | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-dropdown-container')) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // Close mobile menu and reset expanded state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setExpandedMobileMenu(null);
    }
  }, [isMenuOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchKeyword.trim()) {
      console.log('Searching for:', searchKeyword);
      setSearchKeyword('');
      setIsSearchOpen(false);
    }
  };

  const menuItems = [
    {
      id: 1,
      title: '센터소개',
      path: '/about',
      subItems: [
        { title: '인사말', path: '/about/greeting' },
        { title: 'GTC소개', path: '/about/gtc' },
        { title: '시설안내', path: '/about/facilities' },
        { title: '오시는 길', path: '/about/location' }
      ]
    },
    {
      id: 2,
      title: '프로그램',
      path: '/programs',
      subItems: [
        { title: '단체체험', path: '/programs/group' },
        { title: '유치과정', path: '/programs/preschool' },
        { title: '초등과정', path: '/programs/elementary' },
        { title: '중·고등과정', path: '/programs/secondary' },
        { title: '성인과정', path: '/programs/adult' },
        { title: '특별과정', path: '/programs/special' },
        { title: '방학캠프', path: '/programs/camp' },
        { title: '해외 스쿨링', path: '/programs/overseas-schooling' },
        { title: '동두천장학생', path: '/programs/scholarship' }
      ]
    },
    {
      id: 3,
      title: '공지사항',
      path: '/notice',
      subItems: [
        { title: '공지사항', path: '/notice' },
        { title: '교육 자료실', path: '/notice/materials' }
      ]
    },
    {
      id: 4,
      title: '수강신청',
      path: '/application',
      subItems: [
        { title: '레벨 테스트 신청', path: '/application/level-test' },
        { title: '수강신청', path: '/application/register' },
        { title: '단체체험(기관) 신청', path: '/application/group' }
      ]
    },
    {
      id: 5,
      title: '참여마당',
      path: '/community',
      subItems: [
        { title: '자주 묻는 질문', path: '/community/faq' },
        { title: '갤러리', path: '/community/gallery' },
        { title: '수강후기', path: '/community/reviews' },
        { title: '설문조사', path: '/community/survey' }
      ]
    },
    {
      id: 6,
      title: '마이페이지',
      path: '/mypage',
      subItems: [
        { title: '정보수정', path: '/mypage/profile' },
        { title: '신청내역', path: '/mypage/applications' },
        { title: '결제하기', path: '/mypage/payment' },
        { title: '환불안내', path: '/mypage/refund' }
      ]
    }
  ];

  return (
    <>
      <header
        className={`header fixed w-full top-0 z-[100] transition-colors duration-300 ${
          isTransparent && !isHeaderHovered
            ? 'bg-transparent'
            : 'bg-white border-b border-gray-200'
        }`}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <div className="header__inner w-full relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <Link
                to="/"
                className="header__logo absolute left-4 md:left-6 2xl:left-8 top-5 flex items-center gap-2 group z-[110]"
              >
                <img
                  src="/logo.png"
                  alt="DDGTC Logo"
                  className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                />
                <div className="flex flex-col">
                  <span className={`text-xs font-bold tracking-wide transition-colors duration-300 whitespace-nowrap ${
                    isTransparent && !isHeaderHovered ? 'text-white' : 'text-gray-800'
                  }`}>
                    DONGDUCHEON GLOBAL
                  </span>
                  <span className={`text-[10px] font-medium tracking-wide transition-colors duration-300 ${
                    isTransparent && !isHeaderHovered ? 'text-gray-200' : 'text-gray-500'
                  }`}>
                    TALENT CENTER
                  </span>
                </div>
              </Link>

              {/* Right Side Icons */}
              <div className="header__link flex items-center space-x-2 md:space-x-3 absolute right-4 md:right-6 2xl:right-8">
              {/* Search */}
              <div className="search_box relative">
                {!isSearchOpen ? (
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className={`transition-colors duration-300 ${
                      isTransparent && !isHeaderHovered
                        ? 'text-white hover:text-blue-300'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                ) : (
                  <form onSubmit={handleSearch} className="absolute right-0 top-1/2 -translate-y-1/2 z-50">
                    <div className="flex items-center bg-white border border-gray-200 rounded-full px-4 py-2 shadow-lg">
                      <input
                        type="text"
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="txt bg-transparent outline-none text-sm w-48 text-gray-700"
                        placeholder="검색어를 입력하세요"
                        autoFocus
                        onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                      />
                      <button type="submit">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Language */}
              <div className="lang hidden md:block">
                <button className={`transition-colors duration-300 text-sm font-medium ${
                  isTransparent && !isHeaderHovered
                    ? 'text-white hover:text-blue-300'
                    : 'text-gray-600 hover:text-blue-600'
                }`}>
                  ENG
                </button>
              </div>

              {/* User Info */}
              <div className="hidden md:flex items-center space-x-3 relative user-dropdown-container">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsUserDropdownOpen(!isUserDropdownOpen);
                  }}
                  className={`flex items-center space-x-1 text-sm transition-colors duration-300 ${
                    isTransparent && !isHeaderHovered ? 'text-white hover:text-blue-300' : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  <span>{user?.name || '게스트'}님</span>
                  <ChevronDownIcon className="h-4 w-4" />
                </button>

                {isUserDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]">
                    <div className="py-1">
                      {!user && (
                        <>
                          <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">테스트 계정</div>
                          {testStudents.map(student => (
                            <button
                              key={student.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                loginAsStudent(student.id);
                                setIsUserDropdownOpen(false);
                              }}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {student.name}
                            </button>
                          ))}
                          <div className="border-t border-gray-100 mt-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/login');
                                setIsUserDropdownOpen(false);
                              }}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              관리자 로그인
                            </button>
                          </div>
                        </>
                      )}
                      {user && (
                        <>
                          {user.role === 'student' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/mypage');
                                setIsUserDropdownOpen(false);
                              }}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              마이페이지
                            </button>
                          )}
                          {user.role === 'admin' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/admin');
                                setIsUserDropdownOpen(false);
                              }}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              관리자 페이지
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLogout();
                              setIsUserDropdownOpen(false);
                            }}
                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                          >
                            로그아웃
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="2xl:hidden header__btn"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <div className="btn__wrap w-6 h-6 flex flex-col justify-center space-y-1.5">
                  {isMenuOpen ? (
                    <XMarkIcon className={`h-6 w-6 transition-colors duration-300 ${
                      isTransparent && !isHeaderHovered ? 'text-white' : 'text-gray-700'
                    }`} />
                  ) : (
                    <>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                      <span className={`block w-full h-0.5 transition-colors duration-300 ${
                        isTransparent && !isHeaderHovered ? 'bg-white' : 'bg-gray-700'
                      }`}></span>
                    </>
                  )}
                </div>
              </button>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden 2xl:block absolute left-1/2 transform -translate-x-1/2 top-0 h-20 pointer-events-auto z-[100]">
            <ul className="flex m-0 p-0 list-none h-20 gap-8">
              {menuItems.map((item) => (
                <li key={item.id} className="m-0 p-0 flex items-center justify-center" style={{ width: '140px' }}>
                  <Link
                    to={item.path}
                    className={`font-semibold text-[17px] transition-colors duration-300 whitespace-nowrap ${
                      isTransparent && !isHeaderHovered
                        ? 'text-white hover:text-blue-300'
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Dropdown Background - Full Width (Only for 2xl and above) */}
          <div className={`hidden 2xl:block absolute w-screen bg-white border-t border-gray-200 shadow-lg overflow-hidden z-[90] transition-all duration-300`}
            style={{
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: isHeaderHovered ? '300px' : '0',
              paddingTop: isHeaderHovered ? '30px' : '0',
              paddingBottom: isHeaderHovered ? '80px' : '0',
              opacity: isHeaderHovered ? 1 : 0,
              visibility: isHeaderHovered ? 'visible' : 'hidden'
            }}
          >
            <div className="absolute left-1/2 transform -translate-x-1/2 top-[30px]">
              <ul className="flex m-0 p-0 list-none gap-8">
                {menuItems.map((item) => (
                  <li key={item.id} className="m-0 p-0" style={{ width: '140px' }}>
                    <ul className="m-0 p-0 list-none">
                      {item.subItems.map((subItem) => (
                        <li key={subItem.path} className="m-0 p-0 mb-4 last:mb-0">
                          <Link
                            to={subItem.path}
                            className="block text-[16px] font-medium text-gray-600 hover:text-blue-600 transition-colors whitespace-nowrap text-center"
                            onClick={() => setIsHeaderHovered(false)}
                          >
                            {subItem.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      <div
        className={`2xl:hidden fixed left-0 right-0 bg-white border-b border-gray-200 shadow-lg overflow-hidden transition-all duration-300 z-[90]`}
        style={{
          top: '80px',
          maxHeight: isMenuOpen ? '80vh' : '0',
          opacity: isMenuOpen ? 1 : 0
        }}
      >
        <nav className="overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {menuItems.map((item) => (
            <div key={item.id} className="border-b border-gray-100">
              <button
                onClick={() => setExpandedMobileMenu(expandedMobileMenu === item.id ? null : item.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-gray-700 font-medium text-base hover:bg-gray-50"
              >
                <span>{item.title}</span>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    expandedMobileMenu === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 bg-gray-50`}
                style={{
                  maxHeight: expandedMobileMenu === item.id ? `${item.subItems.length * 48}px` : '0'
                }}
              >
                {item.subItems.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className="block px-6 py-3 text-gray-600 text-sm hover:text-blue-600 hover:bg-gray-100"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setExpandedMobileMenu(null);
                    }}
                  >
                    {subItem.title}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="2xl:hidden fixed inset-0 bg-black bg-opacity-20 z-[80]"
          style={{ top: '80px' }}
          onClick={() => {
            setIsMenuOpen(false);
            setExpandedMobileMenu(null);
          }}
        />
      )}
    </>
  );
};

export default Header;
